import { Component } from '@theme/component';
import { isMobileBreakpoint, onAnimationEnd, setLocalStorage, getLocalStorage, setCookie, getCookie, removeLocalStorage, debounce, onIdleAfterDOMReady } from '@theme/utilities';
import { DialogCloseEvent } from '@theme/events';

export class CustomPopupComponent extends Component {
  // Cached DOM elements
  #popupContent;
  #popupMessage;
  #popupForm;
  #floatingButton;
  #floatingButtonContent;
  #floatingButtonClose;
  
  // Component state
  #config;
  #isSubmitted;
  #shouldShowFloatingButton;

  #deselectTimeout;

  connectedCallback() {
    super.connectedCallback();
    this.#setupEventListeners();
    this.#initialize();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#removeEventListeners();
  }

  /**
   * Sets up event listeners for media queries, floating button, and dialog close events.
   */
  #setupEventListeners() {
    const signal = this.abortController.signal;

    this.handleMediaQueryChange = debounce(() => this.#switchPopup(), 50);
    this.handleDialogClose = this.#onDialogClose.bind(this);

    window.addEventListener('resize', this.handleMediaQueryChange, { signal });
    this.addEventListener(DialogCloseEvent.eventName, this.handleDialogClose, { signal });

    if (Shopify.designMode) {
      this.#handleDesignMode();
    }
  }

  /**
   * Removes all event listeners and cleans up timeouts to prevent memory leaks.
   */
  #removeEventListeners() {
    // Abort all event listeners registered with AbortController
    this.abortController.abort();

    // Remove exit intent listener if popup was destroyed before it triggered
    if (this.handleExitIntent) {
      document.removeEventListener('mouseleave', this.handleExitIntent);
      this.handleExitIntent = null;
    }

    // Cleanup timeouts if they exist
    if (this.delayTimeout) {
      clearTimeout(this.delayTimeout);
      this.delayTimeout = null;
    }
    if (this.#deselectTimeout) {
      clearTimeout(this.#deselectTimeout);
      this.#deselectTimeout = null;
    }
  }

  /**
   * Sets up design mode handlers for Shopify theme editor.
   */
  #handleDesignMode() {
    const section = this.closest('.shopify-section');
    if (!section) return;
    
    const signal = this.abortController.signal;

    this.handleSectionSelect = async (event) => {
      clearTimeout(this.#deselectTimeout);
      this.#deselectTimeout = null;
      this.#deselectTimeout = setTimeout(async () => {
        if (section === event.target) {
          this.style.display = 'block';
          if (this.#getFloatingButton() && this.firstAppearance === 'floating_button') {
            event.isDesignMode = true;
            this.popupContent?.closeDialog(event);
            this.popupMessage?.closeDialog(event);
            this.floatingButton?.classList.add('opened');
          } else {
            this.floatingButton?.classList.add('closing');
            this.#shouldShowFloatingButton = false;
            event.isDesignMode = true;
            this.popupMessage?.closeDialog(event);
            await this.popupContent?.openDialog();
            this.floatingButton?.classList.remove('opened', 'closing');
            const storageKey = this.#getStorageKey('cookie');
            removeLocalStorage(storageKey);
          }
        }
      }, 100);
    };
    section.addEventListener('shopify:section:select', this.handleSectionSelect, { signal });

    this.handleSectionDeselect = (event) => {
      if (section === event.target) {
        this.style.removeProperty('display');
        event.isDesignMode = true;
        this.floatingButton?.classList.remove('opened');
        this.popupContent?.closeDialog(event);
        this.popupMessage?.closeDialog(event);
      }
    };
    section.addEventListener('shopify:section:deselect', this.handleSectionDeselect, { signal });

    this.handleSectionLoad = () => {
      this.#setupFloatingButtonDesignMode();
      this.popupContent?.openDialog();
    };
    this.handleSectionUnload = () => {
      this.#setupFloatingButtonDesignMode();
      this.popupContent?.closeDialog();
    };
    section.addEventListener('shopify:section:load', this.handleSectionLoad, { signal });
    section.addEventListener('shopify:section:unload', this.handleSectionUnload, { signal });

    this.handleBlockSelect = async (event) => {
      clearTimeout(this.#deselectTimeout);
      this.#deselectTimeout = null;
      this.style.display = 'block';
      if (this.popupMessage && this.popupMessage.contains(event.target)) {
        this.isDesigningMode = true;
        event.isDesignMode = true;
        this.floatingButton?.classList.add('closing');
        this.popupContent?.closeDialog(event);
        await this.popupMessage?.openDialog();
        this.floatingButton?.classList.remove('closing','opened');
      } else if(this.popupContent && this.popupContent.contains(event.target)) {
        event.isDesignMode = true;
        this.floatingButton?.classList.add('closing');
        this.popupMessage?.closeDialog(event);
        await this.popupContent?.openDialog();
        this.floatingButton?.classList.remove('closing','opened');
      }
    };
    this.addEventListener('shopify:block:select', this.handleBlockSelect, { signal });

    this.handleBlockDeselect = (event) => {
      // Delay so that if the user is switching to another block (deselect → select),
      // handleBlockSelect can cancel this timeout before it runs
      this.#deselectTimeout = setTimeout(async () => {
        this.#deselectTimeout = null;
        event.isDesignMode = true;
        if (this.#getFloatingButton() && this.firstAppearance === 'floating_button') {
          // Return to section-selected state: close popup, show floating button
          this.popupContent?.closeDialog(event);
          this.popupMessage?.closeDialog(event);
          this.floatingButton?.classList.add('opened');
        } else {
          // Return to section-selected state: close success message, open signup form
          this.style.display = 'block';
          this.floatingButton?.classList.add('closing');
          this.#shouldShowFloatingButton = false;
          this.popupMessage?.closeDialog(event);
          await this.popupContent?.openDialog();
          this.floatingButton?.classList.remove('opened', 'closing');
          const storageKey = this.#getStorageKey('cookie');
          removeLocalStorage(storageKey);
        }
      }, 50);
    };

    this.addEventListener('shopify:block:deselect', this.handleBlockDeselect, { signal });
  }

  /**
   * Gets the list of valid floating button positions.
   */
  get floatingButtonPositions() {
    return ['center-left', 'center-right', 'bottom-left', 'bottom-center', 'bottom-right'];
  }

  /**
   * Gets or creates a container for the given position.
   */
  #getOrCreateContainer(position) {
    let container = document.querySelector(`.custom-popup-floating-button-group--${position}`);
    if (!container) {
      container = document.createElement('div');
      container.className = `custom-popup-floating-button-group hidden custom-popup-floating-button-group--${position}`;
      document.body.appendChild(container);
    }
    return container;
  }

  /**
   * Gets the position class from a button.
   */
  #getButtonPosition(button) {
    for (const position of this.floatingButtonPositions) {
      if (button.classList.contains(position)) {
        return position;
      }
    }
    return null;
  }

  /**
   * Appends a cloned button to the container for its position.
   */
  #appendClonedButton(button, container) {
    const id = button.dataset.id;
    if (!id) return;
    if (container.querySelector(`#${CSS.escape(id)}`)) return;

    if(Shopify.designMode) {
      const clone = button.cloneNode(true);
      clone.id = id;
      clone.removeAttribute('data-id');
      container.appendChild(clone);
    } else {
      button.id = id;
      button.removeAttribute('data-id');
      container.appendChild(button);
    }
  }


  /**
   * Sets up the floating button in normal mode.
   */
  async #setupFloatingButton() {
    const floatingButton = this.querySelector('.custom-popup-floating-button');
    if (!floatingButton) return;

    const buttonPosition = this.#getButtonPosition(floatingButton);
    if (!buttonPosition) return;

    const container = this.#getOrCreateContainer(buttonPosition);
    if (!container.contains(floatingButton)) {
      this.#appendClonedButton(floatingButton, container);
    }

    const signal = this.abortController.signal;
    this.floatingButtonClose?.addEventListener?.('click', this.handleCloseFloatingButton.bind(this), { signal });
  }

  /**
   * Sets up the floating button in design mode.
   * Removes all containers, queries all buttons, and recreates containers with clones.
   */
  #setupFloatingButtonDesignMode() {
    // Reset cached references so floatingButton getter re-queries the new clone
    this.#floatingButton = null;
    this.#floatingButtonContent = null;
    this.#floatingButtonClose = null;

    const existingContainers = document.querySelectorAll(`.custom-popup-floating-button-group`);
    if (existingContainers.length > 0) {
      existingContainers.forEach(container => {
        container.remove();
      });
    }

    setTimeout(() => {
      const allFloatingButtons = document.querySelectorAll('.custom-popup-floating-button');
      
      if (allFloatingButtons.length === 0) return;

      this.floatingButtonPositions.forEach(position => {
        const buttons = Array.from(allFloatingButtons).filter(button => 
          button.classList.contains(position)
        );

        if (buttons.length === 0) return;

        const container = this.#getOrCreateContainer(position);
        buttons.forEach(button => {
          this.#appendClonedButton(button, container);
        });
      });
    }, 0);
  }

  /**
   * Sets up popup display triggers based on configuration.
   * Checks for form submission first, then sets up trigger-based display (delay, scroll, or exit intent).
   */
  #initialize() {
    onIdleAfterDOMReady(() => {
      this.#setupTemplate();
      this.#setupFloatingButton();
      
      // Check if popup should show after form submission
      if (this.#checkAndShowOnSubmit()) {
        return;
      }

      if(this.getAttribute('data-show-popup') === 'false') {
        return;
      }
      
      const triggerType = this.config?.triggerType;
      const triggerValue = parseInt(this.config?.triggerValue) || 0;
      
      if (triggerType === 'delay') {
        this.#setupDelayTrigger(triggerValue);
      } else if (triggerType === 'scroll') {
        this.#setupScrollTrigger(triggerValue);
      } else if (triggerType === 'exit_intent') {
        this.#setupExitTrigger();
      }
    });
  }

  /**
   * Sets up the popup template.
   */
  #setupTemplate() {
    const template = this.querySelector('.custom-popup-template');
    if (!template) return;
    this.innerHTML = template.innerHTML;
  }

  /**
   * Sets up exit intent trigger to show popup when user moves mouse out of viewport.
   * Only triggers once, then removes the listener.
   */
  #setupExitTrigger() {
    this.handleExitIntent = () => {
      this.#show();
      document.removeEventListener('mouseleave', this.handleExitIntent);
      this.handleExitIntent = null;
    };
    document.addEventListener('mouseleave', this.handleExitIntent);
  }
  /**
   * Sets up delay trigger to show popup after specified delay, counted from window load
   * so the popup never competes with the initial render.
   * @param {number} delaySeconds - Delay in seconds before showing popup
   */
  #setupDelayTrigger(delaySeconds) {
    const startCountdown = () => {
      this.delayTimeout = setTimeout(() => {
        this.#show();
        this.delayTimeout = null;
      }, delaySeconds * 1000);
    };

    if (document.readyState === 'complete') {
      startCountdown();
    } else {
      window.addEventListener('load', startCountdown, { once: true, signal: this.abortController.signal });
    }
  }

  /**
   * Sets up scroll trigger to show popup when user scrolls past threshold.
   * @param {number} thresholdPercent - Scroll threshold as percentage of document height (0-100)
   */
  #setupScrollTrigger(thresholdPercent) {
    // Get document height (full page height)
    const documentHeight = document.documentElement.scrollHeight;
    // Convert percentage to pixels based on document height
    const thresholdPixels = (documentHeight * thresholdPercent) / 100;
    
    this.handleScroll = () => {
      if (window.scrollY > thresholdPixels) {
        this.#show();
        this.#cleanupScrollListener();
      }
    };
    this.debouncedScrollHandler = debounce(this.handleScroll, 100);
    window.addEventListener('scroll', this.debouncedScrollHandler);
  }

  /**
   * Cleans up scroll listener and debounced handler.
   */
  #cleanupScrollListener() {
    if (this.debouncedScrollHandler) {
      window.removeEventListener('scroll', this.debouncedScrollHandler);
      this.debouncedScrollHandler.cancel?.();
      this.debouncedScrollHandler = null;
    }
    this.handleScroll = null;
  }

  /**
   * Switches popup visibility based on mobile breakpoint and settings.
   * Uses event.matches when called from media query change to avoid race.
   * @param {boolean} [isMobile] - Current mobile state; omit to use isMobileBreakpoint() (e.g. on init).
   */
  #switchPopup(isMobile = isMobileBreakpoint()) {
    if (isMobile && !this.#getBooleanFromConfig('showPopupMobile')) {
      this.#hide();
    } else {
      this.#show();
    }
  }


  /**
   * Opens the appropriate popup dialog (success message or signup form).
   */
  #openPopupDialog() {
    if (this.isSubmitted && this.popupMessage) {
      // Show success message if form was previously submitted
      this.popupMessage.openDialog();
    } else {
      // Show signup form
      this.popupContent?.openDialog();
    }
  }

  /**
   * Shows the popup component.
   * Displays floating button if configured, otherwise opens popup dialog.
   */
  #show() {
    if (this.#hasPopupCookie() || this.isDesigningMode) {
      return;
    }

    // Don't show popup on mobile if disabled
    if (!this.showPopupMobile) return;
    if (this.shouldShowFloatingButton && this.#getFloatingButton()) {
      this.floatingButton?.classList.add('opened');
    } else {
      this.#openPopupDialog();
    }
  }

  /**
   * Checks if popup should be shown after form submission.
   * @returns {boolean} True if popup was shown, false otherwise
   */
  #checkAndShowOnSubmit() {
    const formId = this.popupForm?.getAttribute('id');
    if (!formId) return false;
    if (this.newsletterFormSubmitted && window.location.hash?.includes(formId)) {
      setLocalStorage(this.#getStorageKey('success'), 'true');
      removeLocalStorage(this.#getStorageKey('floating-button'));
      this.#isSubmitted = true;
      this.#shouldShowFloatingButton = false;
      this.#setupTemplate();
      this.#setupFloatingButton();
      this.#openPopupDialog();
      return true;
    }
    
    if (window.location.hash?.includes(formId)) {
      this.#isSubmitted = false;
      this.#setupTemplate();
      this.#setupFloatingButton();
      this.#openPopupDialog();
      return true;
    }
    return false;
  }

  /**
   * Hides the popup component.
   * Closes the appropriate dialog (success message or signup form).
   */
  #hide() {
    if (this.isSubmitted && this.popupMessage) {
      this.popupMessage?.closeDialog();
    } else {
      this.popupContent?.closeDialog();
    }
  }

  /**
   * Opens the popup dialog (public method for external triggers).
   * @param {Event} event - The event that triggered this action
   */
  async openDialog(event) {
    event?.preventDefault();
    this.#hideFloatingButton();
    setTimeout(() => {
      this.#openPopupDialog();
    }, 100);
  }

  /**
   * Handles dialog close event.
   * Shows floating button when popup dialog is closed and sets cookie to prevent showing again.
   * @param {CustomEvent} event - The dialog close event
   */
  #onDialogClose(event) {
    const dialogComponent = event.target;
    if (!dialogComponent || !this.contains(dialogComponent)) return;

    dialogComponent.querySelectorAll('deferred-media').forEach(media => media.pauseMedia?.());

    // Skip handling if this close was triggered from design mode block deselect
    const originalEvent = event.detail?.originalEvent;
    if (originalEvent?.isDesignMode === true) {
      return;
    }

    if (this.#getFloatingButton()) {
      setLocalStorage(this.#getStorageKey('floating-button'), 'true');
      this.#shouldShowFloatingButton = true;
      this.floatingButton?.classList.add('opened');
    } else {
      this.#setPopupCookie();
    }
  }

  /**
   * Handles the close floating button event.
   */
  handleCloseFloatingButton() {
    this.#setPopupCookie();
    this.#hideFloatingButton();
  }

  /**
   * Hides the floating button with animation.
   */
  async #hideFloatingButton() {
    this.floatingButton?.classList.add('closing');
    await onAnimationEnd(this.floatingButtonContent, undefined, {
      subtree: false,
    });
    this.floatingButton?.classList.remove('closing', 'opened');
  }

  /**
   * Checks if the newsletter form was successfully submitted.
   * @returns {Element|null} The success message element if found, null otherwise
   */
  get newsletterFormSubmitted() {
    return this.popupForm?.querySelector('.form__success');
  }

  /**
   * Gets a boolean value from config, handling both boolean and string representations.
   * @param {string} key - The config key to retrieve
   * @returns {boolean} The boolean value
   */
  #getBooleanFromConfig(key) {
    const value = this.config?.[key];
    return value === true || value === 'true';
  }


  /**
   * Gets the floating button display setting from config.
   * @returns {boolean} True if floating button should be shown
   */

  #getFloatingButton() {
    if (isMobileBreakpoint()) {
      return this.#getBooleanFromConfig('showFloatingButtonMobile');
    }
    return this.#getBooleanFromConfig('showFloatingButton');
  }

   /**
   * Generates a storage key for the given type.
   * @param {string} type - The storage type (e.g., 'success', 'floating-button')
   * @returns {string} The storage key
   */
   #getStorageKey(type) {
    return `newsletter-popup-${type}-${this.id}`;
  }

  /**
   * Gets the cookie name for this popup instance.
   * @returns {string} The cookie name
   */
  #getCookieName() {
    return `newsletter-popup-${this.id}`;
  }

  /**
   * Sets a cookie to prevent popup from showing again for the specified number of days.
   */
  #setPopupCookie() {
    const repeatAfter = parseInt(this.config?.repeatAfter) || 1;
    const cookieName = this.#getCookieName();
    
    if (Shopify.designMode) {
      const storageKey = this.#getStorageKey('cookie');
      setLocalStorage(storageKey, 'true', repeatAfter);
    } else {
      setCookie(cookieName, 'true', repeatAfter);
    }
    
    removeLocalStorage(this.#getStorageKey('floating-button'));
    removeLocalStorage(this.#getStorageKey('success'));
  }

  /**
   * Checks if popup cookie exists (user has already seen the popup).
   * @returns {boolean} True if cookie exists and hasn't expired, false otherwise
   */
  #hasPopupCookie() {
    if (Shopify.designMode) {
      const storageKey = this.#getStorageKey('cookie');
      return getLocalStorage(storageKey) === 'true';
    }
    return getCookie(this.#getCookieName()) === 'true';
  }

  /**
   * Gets the component configuration from data attribute.
   * @returns {Object} The parsed configuration object
   */
  get config() {
    if (this.#config) {
      return this.#config;
    }
    const configAttr = this.getAttribute('data-config');
    try {
      this.#config = configAttr ? JSON.parse(configAttr) : {};
    } catch (e) {
      console.error('Failed to parse newsletter popup config:', e);
      this.#config = {};
    }
    return this.#config;
  }

  /**
   * Gets the popup content dialog element (lazy-loaded).
   * @returns {HTMLElement|null} The popup content element
   */
  get popupContent() {
    return this.#popupContent ??= this.querySelector('.custom-popup-content');
  }

  /**
   * Gets the success message dialog element (lazy-loaded).
   * @returns {HTMLElement|null} The success message element
   */
  get popupMessage() {
    return this.#popupMessage ??= this.querySelector('.newsletter-popup-success-message');
  }

  /**
   * Gets the newsletter form element (lazy-loaded).
   * @returns {HTMLElement|null} The form element
   */
  get popupForm() {
    return this.#popupForm ??= this.querySelector('.email-signup__form');
  }

  /**
   * Gets the floating button element (lazy-loaded).
   * @returns {HTMLElement|null} The floating button element
   */
  get floatingButton() {
    return this.#floatingButton ??= document.getElementById(`custom-popup-floating-button-${this.dataset.id}`);
  }

  /**
   * Gets the floating button content element (lazy-loaded).
   * @returns {HTMLElement|null} The floating button content element
   */
  get floatingButtonContent() {
    return this.#floatingButtonContent ??= this.floatingButton?.querySelector('.custom-popup-floating-button-content');
  }

  /**
   * Gets the floating button close button element (lazy-loaded).
   * @returns {HTMLElement|null} The close button element
   */
  get floatingButtonClose() {
    return this.#floatingButtonClose ??= this.floatingButton?.querySelector('.custom-popup-floating-button-close');
  }

  /**
   * Checks if the newsletter form has been submitted (lazy-loaded from localStorage).
   * @returns {boolean} True if form was submitted
   */
  get isSubmitted() {
    if (this.#isSubmitted === undefined) {
      this.#isSubmitted = getLocalStorage(this.#getStorageKey('success')) === 'true';
    }
    return this.#isSubmitted;
  }

  /**
   * Determines if floating button should be shown (lazy-loaded from localStorage).
   * @returns {boolean} True if floating button should be displayed
   */
  get shouldShowFloatingButton() {
    if (this.#shouldShowFloatingButton === undefined) {
      this.#shouldShowFloatingButton = getLocalStorage(this.#getStorageKey('floating-button')) === 'true' || this.floatingButton?.firstAppearance === 'floating_button';
    }
    return this.#shouldShowFloatingButton;
  }

  /**
   * Determines if popup should be shown on mobile devices.
   * @returns {boolean} True if popup should be shown on mobile
   */
  get showPopupMobile() {
    return isMobileBreakpoint() ? this.#getBooleanFromConfig('showPopupMobile') : true;
  }

  /**
   * Gets the first appearance type (popup or floating button).
   * @returns {string} 'popup' or 'floating_button'
   */
  get firstAppearance() {
    return this.config?.firstAppearance || 'popup';
  }

}

if (!customElements.get('custom-popup')) {
  customElements.define('custom-popup', CustomPopupComponent);
}

class CustomPopupButtonElement extends Component {
  connectedCallback() {
    super.connectedCallback();
    if (!this.hasAttribute('role')) this.setAttribute('role', 'button');
    if (!this.hasAttribute('tabindex')) this.setAttribute('tabindex', '0');

    const signal = this.abortController.signal;
    this.addEventListener('keydown', this.#handleKeydown, { signal });
  }

  #handleKeydown = (e) => {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      this.click();
    }
  };
}

if (!customElements.get('custom-popup-button')) {
  customElements.define('custom-popup-button', CustomPopupButtonElement);
}